import React, { useEffect, useState, useRef, useCallback } from "react";
import "./Mid-Section-Pc.scss";

//imports for user data
import { useSelector, useDispatch } from "react-redux";
import { sendCustomBulkSms } from "../../../../redux/slices/sliceActions";

//import components
import customToasts from "../../../assets/reusableComponents/myToasts";

//import icons
import { IoChatbubbles } from "react-icons/io5";
import { TbDeviceMobileMessage } from "react-icons/tb";
import OnlineChatList from "./OnlineChatList";
import OnlineChatMessages from "./OnlineChatMessages";
import MobileChatMessages from "./MobileChatMessages"; // New component for mobile messages

// Import the utility functions
import {
  formatTimestamp,
  formatDate,
  getLastMessageSnippet,
  getLastMessageTimestamp,
  storeConversation, // Import the new localStorage functions
  getStoredConversations,
} from "./utils/ChatUtils";
import { Bounce, ToastContainer } from "react-toastify";

const MessagingApp = () => {
  const dispatch = useDispatch();
  const [messageInput, setMessageInput] = useState("");
  const [activeChat, setActiveChat] = useState("");
  const [activeChatId, setActiveChatId] = useState(""); // For storing the active chat's user ID
  const [activeChatType, setActiveChatType] = useState("online"); // "online" or "mobile"
  const [mobileRecipients, setMobileRecipients] = useState([]); // For storing mobile message recipients
  const [mobileMessageText, setMobileMessageText] = useState(""); // For mobile broadcast message
  const [message_type, set_message_type] = useState("sms"); // Default message type
  const [selectedProperty, setSelectedProperty] = useState("");

  const { user, properties, message } = useSelector(
    (state) => state.landlordslice
  );

  //web socket functionality
  const socketRef = useRef(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const maxReconnectAttempts = 5;
  const reconnectTimeouts = [1000, 2000, 4000, 8000, 16000]; // Exponential backoff

  // Process properties to create chats organized by property
  const createPropertyBasedChats = () => {
    if (!properties || properties.length === 0) {
      return [
        {
          propertyName: "Default",
          users: [
            {
              user: "No role takers",
              isPlaceholder: true,
              messages: [],
            },
          ],
        },
      ];
    }

    // Transform properties into property-based chat groups
    return properties.map((property) => {
      const propertyChats = {
        propertyName: property.name,
        propertyId: property._id,
        users: [],
      };

      // Add role takers if they exist
      if (property.role_takers && property.role_takers.length > 0) {
        property.role_takers.forEach((roleTaker) => {
          if (roleTaker.name) {
            propertyChats.users.push({
              user: roleTaker?.name || `Role Taker ${roleTaker._id}`,
              displayName: `${roleTaker?.name} (${roleTaker.role || "Staff"})`,
              userId: roleTaker?._id,
              role: roleTaker.role || "Staff",
              userType: "staff",
              profilePicture:
                roleTaker?.image && roleTaker?.image.length > 0
                  ? roleTaker?.image[0]
                  : "",
              messages: [
                {
                  text: `Hello! I'm a ${roleTaker.role || "staff member"} for ${
                    property.name
                  }.`,
                  type: "incoming",
                  timestamp: new Date().toISOString(),
                },
              ],
            });
          }
        });
      }

      // Add tenants from houses
      if (property.houses && property.houses.length > 0) {
        property.houses.forEach((house) => {
          if (house.tenant && house.tenant.user) {
            propertyChats.users.push({
              user: house.tenant.user.name || `Tenant ${house.house_no}`,
              displayName: `${house.tenant.user.name || "Tenant"} (House ${
                house.house_no
              })`,
              userId: house.tenant.user._id,
              role: "Tenant",
              userType: "tenant",
              houseNo: house.house_no,
              profilePicture: house.tenant.user.profile_picture || "",
              messages: [
                {
                  text: `Hello! I'm a tenant in house ${house.house_no}.`,
                  type: "incoming",
                  timestamp: new Date().toISOString(),
                },
              ],
            });
          }
        });
      }

      // If no users were added, add a placeholder message
      if (propertyChats.users.length === 0) {
        propertyChats.users.push({
          user: "No users",
          isPlaceholder: true,
          messages: [],
        });
      }

      return propertyChats;
    });
  };

  const [chats, setChats] = useState(createPropertyBasedChats());
  useEffect(() => {
    console.log("Chats updated:", chats);
  }, [chats]);
  // Initialize conversations state with stored values from localStorage
  const [conversations, setConversations] = useState(() =>
    getStoredConversations()
  );

  // Reconnect function with exponential backoff
  const reconnectWebSocket = useCallback(() => {
    if (reconnectAttempts >= maxReconnectAttempts) {
      setIsError(true);
      setErrorMessage("Failed to reconnect after multiple attempts");
      return;
    }

    const timeout = reconnectTimeouts[reconnectAttempts] || 16000;
    setTimeout(() => {
      connectWebSocket();
      setReconnectAttempts((prev) => prev + 1);
    }, timeout);
  }, [reconnectAttempts]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    setIsLoading(true);

    const webSocketUrl = process.env.REACT_APP_WEB_SOCKET_ENDPOINT;
    console.log("Connecting to WebSocket:", webSocketUrl);
    const socket = new WebSocket(webSocketUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log("WebSocket connected");
      setIsLoading(false);
      setIsError(false);
      setReconnectAttempts(0); // Reset reconnect attempts on successful connection

      // Always authenticate after connection is established
      authenticateWebSocket();
    };

    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        console.log("Message received:", msg);

        handleWebSocketMessage(msg);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    socket.onclose = (event) => {
      console.log("WebSocket disconnected:", event.reason);
      setIsAuthenticated(false);
      setIsLoading(false);

      // Attempt to reconnect
      // reconnectWebSocket();
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      setIsError(true);
      setErrorMessage("Connection error occurred");
      setIsLoading(false);

      // Close the socket to ensure onclose is triggered
      socket.close();
    };
  }, [reconnectWebSocket]);

  // Authentication function
  const authenticateWebSocket = () => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.log("Cannot authenticate: WebSocket is not open");
      return;
    }

    const userId = user?.data?.id;
    if (!userId) {
      console.error("Cannot authenticate: No user ID available");
      return;
    }

    const authPayload = {
      msg_type: "user.auth",
      msg_res: {
        message: "Authenticating WebSocket",
        data: {
          user_id: userId,
        },
      },
    };

    socketRef.current.send(JSON.stringify(authPayload));
  };

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = (msg) => {
    const { msg_type, msg_res } = msg;

    switch (msg_type) {
      case "user.auth.ack":
        setIsAuthenticated(true);
        setIsSuccess(true);
        console.log("Authentication successful");

        // Store all conversations from authentication response
        if (
          msg_res &&
          msg_res.data &&
          Array.isArray(msg_res.data.conversations)
        ) {
          updateStoredConversations(msg_res.data.conversations);
        }
        break;

      case "user.message.received":
        handleNewMessage(msg_res.data);
        break;

      case "user.message.sent.ack":
        handleMessageSentAck(msg_res.data);
        break;

      case "user.message.broadcast.ack":
        handleBroadcastMessageAck(msg_res.data);
        break;

      case "conversation.history.response":
        handleConversationHistory(msg_res.data);
        break;

      case "error":
        handleError(msg_res);
        break;

      default:
        console.log("Unhandled message type:", msg_type);
    }
  };

  // New function to update stored conversations from authentication response
  const updateStoredConversations = (conversationsData) => {
    try {
      // Create a conversations object mapping user IDs to conversation IDs
      const conversationsMap = {};

      conversationsData.forEach((conversation) => {
        // Only store conversations with a valid other_user_id
        if (conversation.other_user_id && conversation.conversation_id) {
          conversationsMap[conversation.other_user_id] =
            conversation.conversation_id;
        }
      });

      // Update local state
      setConversations((prevConversations) => ({
        ...prevConversations,
        ...conversationsMap,
      }));

      // Update localStorage by merging with existing data
      const existingConversations =
        JSON.parse(localStorage.getItem("rently_conversations")) || {};
      const updatedConversations = {
        ...existingConversations,
        ...conversationsMap,
      };

      localStorage.setItem(
        "rently_conversations",
        JSON.stringify(updatedConversations)
      );
      console.log(
        `Updated ${
          Object.keys(conversationsMap).length
        } conversations in localStorage`
      );
    } catch (error) {
      console.error("Error updating stored conversations:", error);
    }
  };

  // Handle new incoming messages
  const handleNewMessage = (data) => {
    const { sender_id, content, conversation_id, createdAt } = data;

    // Store the conversation ID for this sender (in state and localStorage)
    setConversations((prev) => {
      const updated = { ...prev, [sender_id]: conversation_id };
      // Store in localStorage
      storeConversation(sender_id, conversation_id);
      return updated;
    });

    // Update the chat UI with the new message
    const newMessage = {
      text: content,
      type: "incoming",
      timestamp: createdAt || new Date().toISOString(),
    };

    // Update the chats state to include this new message
    setChats((prevChats) =>
      prevChats.map((propertyChat) => {
        const updatedUsers = propertyChat.users.map((chat) =>
          chat.userId === sender_id
            ? { ...chat, messages: [...chat.messages, newMessage] }
            : chat
        );
        return { ...propertyChat, users: updatedUsers };
      })
    );
  };

  // Handle message sent acknowledgments
  const handleMessageSentAck = (data) => {
    const { recipient_id, content, conversation_id, createdAt } = data;

    // Store the conversation ID for this recipient (in state and localStorage)
    setConversations((prev) => {
      const updated = { ...prev, [recipient_id]: conversation_id };
      // Store in localStorage
      storeConversation(recipient_id, conversation_id);
      return updated;
    });

    console.log(
      `Message sent successfully. Conversation ID: ${conversation_id}`
    );
  };

  // Handle user.message.broadcast.ack response
  const handleBroadcastMessageAck = (data) => {
    const { results } = data;

    // Store conversation IDs for each successful recipient
    if (results && Array.isArray(results)) {
      results.forEach((result) => {
        if (result.success && result.recipient_id && result.conversation_id) {
          setConversations((prev) => {
            const updated = {
              ...prev,
              [result.recipient_id]: result.conversation_id,
            };
            // Store in localStorage
            storeConversation(result.recipient_id, result.conversation_id);
            return updated;
          });
        }
      });
    }

    console.log("Broadcast message sent successfully");
  };

  // Handle conversation history responses
  const handleConversationHistory = (data) => {
    console.log("messages:", data);
    const { conversation_id, messages } = data;
    console.log("Received conversation history:", messages);

    // Find the user associated with this conversation
    const userId = Object.keys(conversations).find(
      (key) => conversations[key] === conversation_id
    );

    if (userId) {
      // Format messages for the UI
      const formattedMessages = messages.map((msg) => {
        console.log(
          "Comparing sender_id:",
          msg.sender_id,
          "with user ID:",
          user?.data?.id
        );
        return {
          text: msg.content,
          type:
            String(msg.sender_id) === String(user?.data?.id)
              ? "outgoing"
              : "incoming",
          timestamp: msg.createdAt,
        };
      });

      // Log the formatted messages
      console.log("Formatted messages:", formattedMessages);

      // Update the chat with the history
      setChats((prevChats) =>
        prevChats.map((propertyChat) => {
          const updatedUsers = propertyChat.users.map((chat) => {
            // Match by userId instead of username for more reliable matching
            if (chat.userId === userId) {
              console.log("Updating messages for user:", chat.user);

              // Keep the initial welcome message if it exists
              let updatedMessages = [...formattedMessages];

              // Look for the welcome message that starts with "Hello! I'm a"
              const welcomeMessage = chat.messages.find(
                (msg) => msg.text && msg.text.includes("Hello! I'm a")
              );

              // If welcome message exists, add it at the beginning
              if (welcomeMessage) {
                updatedMessages = [welcomeMessage, ...updatedMessages];
              }

              console.log("Final message count:", updatedMessages.length);
              return { ...chat, messages: updatedMessages };
            }
            return chat;
          });
          return { ...propertyChat, users: updatedUsers };
        })
      );
    } else {
      console.log("Could not find user for conversation ID:", conversation_id);
    }
  };

  // Handle WebSocket errors
  const handleError = (errorData) => {
    console.error("WebSocket error:", errorData.message);
    setIsError(true);
    setErrorMessage(errorData.message || "An error occurred");
  };

  // Send a message via WebSocket
  const sendMessage = (recipientId, messageText) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setIsError(true);
      setErrorMessage("Connection lost. Reconnecting...");
      connectWebSocket();
      return;
    }

    if (!isAuthenticated) {
      authenticateWebSocket();
      setIsError(true);
      setErrorMessage("Not authenticated. Please try again.");
      return;
    }

    const messagePayload = {
      msg_type: "user.message",
      msg_res: {
        message: "Sending message",
        data: {
          recipient_id: recipientId,
          message: messageText,
        },
      },
    };

    socketRef.current.send(JSON.stringify(messagePayload));
  };

  // Send initial message to create conversation
  const sendInitialMessage = (recipientId) => {
    const genericMessage = "Hello! I'm connecting to start a conversation.";
    sendMessage(recipientId, genericMessage);

    // Add a temporary outgoing message to the UI
    const newMessage = {
      text: genericMessage,
      type: "outgoing",
      timestamp: new Date().toISOString(),
    };

    setChats((prevChats) =>
      prevChats.map((propertyChat) => {
        const updatedUsers = propertyChat.users.map((chat) =>
          chat.userId === recipientId
            ? { ...chat, messages: [...chat.messages, newMessage] }
            : chat
        );
        return { ...propertyChat, users: updatedUsers };
      })
    );
  };

  // Send a broadcast message to multiple recipients
  const sendBroadcastMessage = (recipientIds, messageText) => {
    // console.log("reached sendBroadcastMessage",recipientIds, messageText)
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setIsError(true);
      setErrorMessage("Connection lost. Reconnecting...");
      connectWebSocket();
      return;
    }

    if (!isAuthenticated) {
      authenticateWebSocket();
      setIsError(true);
      setErrorMessage("Not authenticated. Please try again.");
      return;
    }

    const broadcastPayload = {
      msg_type: "user.message.broadcast",
      msg_res: {
        message: "Broadcasting message",
        data: {
          recipient_ids: recipientIds,
          message: messageText,
        },
      },
    };

    socketRef.current.send(JSON.stringify(broadcastPayload));
  };

  // Send bulk SMS to multiple recipients
  const sendBulkSMS = (recipientIds, messageText, message_type) => {
    customToasts
      .promise(
        dispatch(
          sendCustomBulkSms({
            tenant_ids: recipientIds,
            message: messageText,
            message_type,
            selectedProperty,
          })
        ).unwrap(),
        {
          pending: "Sending SMS messages...",
          success: "SMS messages sent successfully!",
          error: "Failed to send SMS messages.",
        }
      )
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error sending bulk SMS:", error);
        setIsError(true);
        setErrorMessage("Failed to send SMS messages.");
      });
  };

  // Fetch conversation history
  const fetchConversationHistory = (conversationId) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setIsError(true);
      setErrorMessage("Connection lost. Reconnecting...");
      connectWebSocket();
      return;
    }

    if (!isAuthenticated) {
      authenticateWebSocket();
      setIsError(true);
      setErrorMessage("Not authenticated. Please try again.");
      return;
    }

    const historyPayload = {
      msg_type: "conversation.history.request",
      msg_res: {
        message: "Requesting conversation history",
        data: {
          conversation_id: conversationId,
        },
      },
    };

    socketRef.current.send(JSON.stringify(historyPayload));
  };

  // Mark a conversation as read
  const markConversationAsRead = (conversationId) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    const markReadPayload = {
      msg_type: "conversation.markread.request",
      msg_res: {
        message: "Marking conversation as read",
        data: {
          conversation_id: conversationId,
        },
      },
    };

    socketRef.current.send(JSON.stringify(markReadPayload));
  };

  // Update chats when properties change
  useEffect(() => {
    setChats(createPropertyBasedChats());
  }, [properties]);

  // Connect WebSocket when component mounts
  useEffect(() => {
    if (user?.data?.id) {
      connectWebSocket();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        console.log("WebSocket disconnected due to component unmount.");
      }
    };
  }, [user, connectWebSocket]);

  // Input handling for the chat
  const handleInputChange = (event) => {
    setMessageInput(event.target.value);
  };

  // Send message function for the chat UI
  const handleSendMessage = () => {
    if (messageInput.trim() && activeChatId) {
      // Create the message object for UI update
      const newMessage = {
        text: messageInput,
        type: "outgoing",
        timestamp: new Date().toISOString(),
      };

      // Add message to UI
      const updatedChats = chats.map((propertyChat) => {
        const updatedUsers = propertyChat.users.map((chat) =>
          chat.userId === activeChatId
            ? { ...chat, messages: [...chat.messages, newMessage] }
            : chat
        );
        return { ...propertyChat, users: updatedUsers };
      });

      setChats(updatedChats);

      // Send message via WebSocket
      sendMessage(activeChatId, messageInput);

      setMessageInput("");
    }
  };

  // Handle mobile broadcast message
  const handleMobileBroadcast = () => {
    const isAutoTargetedMessage =
      message_type === "rent_reminder" || message_type === "late_rent_reminder";

    // if (mobileRecipients.length > 0 && mobileMessageText.trim()) {
    //   sendBroadcastMessage(mobileRecipients, mobileMessageText);
    //   sendBulkSMS(mobileRecipients, mobileMessageText, message_type);
    //   setMobileMessageText("");
    //   setMobileRecipients([]);
    // }
    if (
      (mobileRecipients.length > 0 || isAutoTargetedMessage) &&
      mobileMessageText.trim()
    ) {
      // For auto-targeted, pass property and message only
      if (isAutoTargetedMessage) {
        sendBulkSMS([], mobileMessageText, message_type); // Pass empty array or property only
      } else {
        sendBroadcastMessage(mobileRecipients, mobileMessageText);
        sendBulkSMS(mobileRecipients, mobileMessageText, message_type);
      }
      setMobileMessageText("");
      setMobileRecipients([]);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSendMessage();
    }
  };

  // Select a chat to display
  const handleChatSelect = (user, userID) => {
    setActiveChat(user);
    setActiveChatId(userID);

    // Check if we have a stored conversation ID for this user
    if (conversations[userID]) {
      fetchConversationHistory(conversations[userID]);
      markConversationAsRead(conversations[userID]);
    } else {
      // If no conversation exists, send an initial message to create one
      console.log(
        "No existing conversation found for this user. Sending initial message..."
      );
      console.log("User ID:", userID);
      sendInitialMessage(userID);
    }
  };

  // Switch between online chat and mobile messaging
  const handleChatTypeChange = (type) => {
    setActiveChatType(type);
    setActiveChat("");
  };

  // Find the current chat across all properties
  const findCurrentChat = () => {
    for (const propertyChat of chats) {
      const chat = propertyChat.users.find((chat) => chat.user === activeChat);
      if (chat) return chat;
    }
    return null;
  };

  const currentChat = findCurrentChat();

  return (
    <div className="messaging-app-pc">
      <div className="chat-container">
        <aside className="chat-sidebar">
          <div className="chat-select-type">
            <div
              className={`chat-type ${
                activeChatType === "online" ? "selected" : ""
              }`}
              onClick={() => handleChatTypeChange("online")}
            >
              <IoChatbubbles size={25} /> Online chat
            </div>
            <div
              className={`chat-type ${
                activeChatType === "mobile" ? "selected" : ""
              }`}
              onClick={() => handleChatTypeChange("mobile")}
            >
              <TbDeviceMobileMessage size={25} />
              Mobile messages
            </div>
          </div>

          {activeChatType === "online" && (
            <OnlineChatList
              chats={chats}
              activeChat={activeChat}
              handleChatSelect={handleChatSelect}
              getLastMessageSnippet={getLastMessageSnippet}
              getLastMessageTimestamp={getLastMessageTimestamp}
            />
          )}

          {activeChatType === "mobile" && (
            <div className="mobile-message-panel">
              <h3>SMS Broadcast</h3>
              <p>Select recipients and send messages to their mobile phones</p>
              {/* Mobile messaging interface will go here */}
              {/* This will be implemented in a new component */}
            </div>
          )}
        </aside>

        {activeChatType === "online" && (
          <>
            {activeChat === "" ? (
              <div className="no-chat-selected">
                Please select a chat to start messaging.
              </div>
            ) : (
              <OnlineChatMessages
                currentChat={currentChat}
                messageInput={messageInput}
                handleInputChange={handleInputChange}
                handleSendMessage={handleSendMessage}
                handleKeyPress={handleKeyPress}
                formatTimestamp={formatTimestamp}
                formatDate={formatDate}
              />
            )}
          </>
        )}

        {activeChatType === "mobile" && (
          <MobileChatMessages
            properties={properties}
            mobileMessageText={mobileMessageText}
            setMobileMessageText={setMobileMessageText}
            mobileRecipients={mobileRecipients}
            setMessageTypePassed={set_message_type}
            setMobileRecipients={setMobileRecipients}
            handleMobileBroadcast={handleMobileBroadcast}
            isLoading={isLoading}
            isError={isError}
            errorMessage={errorMessage}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
          />
        )}
      </div>

      {isLoading && (
        <div className="loading-container">
          <div className="message">Authenticating ...</div>
          <div className="loader"></div>
        </div>
      )}

      {isError && (
        <div className="error-container">
          <div className="message">{errorMessage}</div>
        </div>
      )}

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  );
};

export default MessagingApp;
